import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { User, Building2, Briefcase, Crown, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, loading, refreshUser, isPremium } = useAuth();
  const navigate = useNavigate();
  const [jobFunctions, setJobFunctions] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    job_function: '',
    company_type: ''
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        organization: user.organization || '',
        job_function: user.job_function || '',
        company_type: user.company_type || ''
      });
    }
    fetchReferenceData();
  }, [user]);

  const fetchReferenceData = async () => {
    try {
      const [jobsRes, typesRes] = await Promise.all([
        axios.get(`${API}/job-functions`),
        axios.get(`${API}/company-types`)
      ]);
      setJobFunctions(jobsRes.data);
      setCompanyTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/profile`, formData);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelMembership = async () => {
    setCancelling(true);
    
    try {
      await axios.post(`${API}/membership/cancel`);
      await refreshUser();
      toast.success('Membership cancelled successfully');
      setShowCancelDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setCancelling(false);
    }
  };

  const getJobFunctionLabel = (value) => {
    const job = jobFunctions.find(j => j.value === value);
    return job ? job.label : value;
  };

  const getCompanyTypeLabel = (value) => {
    const type = companyTypes.find(t => t.value === value);
    return type ? type.label : value;
  };

  const getMembershipLabel = (tier) => {
    const labels = {
      'free': 'Complimentary',
      'attendee_premium': 'Attendee Premium',
      'business_premium': 'Business Premium'
    };
    return labels[tier] || tier;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-secondary/30 py-12">
      <div className="container-custom max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold mb-2">My Profile</h1>
          <p className="text-muted-foreground">Manage your account information and membership</p>
        </div>

        <div className="grid gap-6">
          {/* Profile Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-gold" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal and professional information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Your full name"
                      data-testid="profile-full-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={user.email}
                      disabled
                      className="bg-muted"
                      data-testid="profile-email"
                    />
                    <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="organization"
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        placeholder="Your company or organization"
                        className="pl-10"
                        data-testid="profile-organization"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="job_function">Job Function</Label>
                    <Select
                      value={formData.job_function}
                      onValueChange={(value) => setFormData({ ...formData, job_function: value })}
                    >
                      <SelectTrigger data-testid="profile-job-function">
                        <Briefcase className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobFunctions.map((job) => (
                          <SelectItem key={job.value} value={job.value}>
                            {job.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="company_type">Company Type</Label>
                    <Select
                      value={formData.company_type}
                      onValueChange={(value) => setFormData({ ...formData, company_type: value })}
                    >
                      <SelectTrigger data-testid="profile-company-type">
                        <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Select your company type" />
                      </SelectTrigger>
                      <SelectContent>
                        {companyTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-gold text-primary hover:bg-gold/90"
                    data-testid="save-profile-btn"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Membership Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-gold" />
                Membership Status
              </CardTitle>
              <CardDescription>
                View and manage your membership
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Plan</p>
                    <p className="text-xl font-semibold flex items-center gap-2">
                      {getMembershipLabel(user.membership_tier)}
                      {isPremium() && <Crown className="w-5 h-5 text-gold" />}
                    </p>
                  </div>
                  {!isPremium() && (
                    <Button 
                      onClick={() => navigate('/membership')}
                      className="bg-gold text-primary hover:bg-gold/90"
                      data-testid="upgrade-membership-btn"
                    >
                      Upgrade
                    </Button>
                  )}
                </div>

                {isPremium() && user.membership_expires && (
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Membership Expires</p>
                      <p className="font-medium">
                        {format(parseISO(user.membership_expires), 'MMMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                )}

                {isPremium() && (
                  <div className="pt-4 border-t border-border">
                    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="text-destructive border-destructive hover:bg-destructive/10"
                          data-testid="cancel-membership-btn"
                        >
                          Cancel Membership
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-destructive" />
                            Cancel Membership
                          </DialogTitle>
                          <DialogDescription>
                            Are you sure you want to cancel your {getMembershipLabel(user.membership_tier)} membership?
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                          <p className="text-sm text-muted-foreground mb-4">
                            If you cancel, you will:
                          </p>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start gap-2">
                              <span className="text-destructive">✕</span>
                              <span>Lose access to premium features immediately</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-destructive">✕</span>
                              <span>Be downgraded to our Complimentary tier</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-destructive">✕</span>
                              <span>Not receive a refund for the current billing period</span>
                            </li>
                          </ul>
                          <p className="text-sm text-muted-foreground mt-4">
                            You can always resubscribe later if you change your mind.
                          </p>
                        </div>
                        <DialogFooter>
                          <Button 
                            variant="outline" 
                            onClick={() => setShowCancelDialog(false)}
                            data-testid="cancel-dialog-back"
                          >
                            Keep Membership
                          </Button>
                          <Button 
                            variant="destructive" 
                            onClick={handleCancelMembership}
                            disabled={cancelling}
                            data-testid="confirm-cancel-btn"
                          >
                            {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {!isPremium() && (
                  <div className="p-4 bg-gold/10 border border-gold/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold mt-0.5" />
                      <div>
                        <p className="font-medium">Upgrade to Premium</p>
                        <p className="text-sm text-muted-foreground">
                          Get early access to events, member directory access, and more premium features.
                        </p>
                        <Button 
                          onClick={() => navigate('/membership')}
                          variant="link"
                          className="text-gold p-0 h-auto mt-2"
                        >
                          View membership options →
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Account Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Account Type</p>
                  <p className="font-medium capitalize">{user.user_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Member Since</p>
                  <p className="font-medium">
                    {format(parseISO(user.created_at), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
